import { useMemo } from 'react';
import useMarketSelectorPerpMarkets from 'src/components/MarketSelector/hooks/useMarketSelectorPerpMarkets';
import { perpMarketCategoryFilter } from 'src/components/MarketSelector/utils';
import { MarketCategory, MARKET_TAGS } from 'src/constants/markets';
import {
	PerpMarketConfig,
	BigNum,
	PRICE_PRECISION_EXP,
} from '@velocity-exchange/sdk';
import { MarketSelectorSort } from 'src/components/MarketSelector/types';

interface Params {
	selectedCategory: MarketCategory;
	searchValue: string;
	sort: MarketSelectorSort | undefined;
	isFavoritesSticky: boolean;
}

export default function useMarketSelectorDisplayedMarkets({
	selectedCategory,
	searchValue,
	sort,
	isFavoritesSticky,
}: Params) {
	// Spot trading is disabled, so the market selector only lists perp markets.
	const defaultSortedPerpMarkets = useMarketSelectorPerpMarkets();

	return useMemo(() => {
		const isAllMarkets = selectedCategory === 'all';
		const allMarkets = [...defaultSortedPerpMarkets];

		const categoryFilteredMarkets = (() => {
			if (isAllMarkets) {
				return allMarkets;
			}

			return allMarkets.filter((market) =>
				perpMarketCategoryFilter(
					market.market.market as PerpMarketConfig,
					selectedCategory,
					market.isHot ?? false
				)
			);
		})();

		const searchFilteredMarkets = (() => {
			if (!searchValue) {
				return categoryFilteredMarkets;
			}

			return categoryFilteredMarkets.filter((market) => {
				let tags = [
					market.symbol.toLowerCase(),
					market.baseAssetSymbol.toLowerCase(),
				];

				const isPerpMarket = market.market.isPerp;

				tags = tags.concat(
					(isPerpMarket
						? MARKET_TAGS.perp[
								market.market.marketIndex as keyof typeof MARKET_TAGS.perp
							]
						: []) ?? []
				);

				if (isPerpMarket) {
					tags.push(
						(
							market.market.market as PerpMarketConfig
						)?.fullName?.toLowerCase() ?? ''
					);
				}

				return tags.some((tag) => tag.includes(searchValue.toLowerCase()));
			});
		})();

		const sortedMarkets = searchFilteredMarkets; // naturally split between perp and spot already

		if (sort) {
			const { header, isDescending } = sort;
			sortedMarkets.sort((a, b) => {
				if (isFavoritesSticky && a.isFavorite !== b.isFavorite) {
					return a.isFavorite ? -1 : 1;
				}

				if (header === 'market') {
					return isDescending
						? b.symbol.localeCompare(a.symbol)
						: a.symbol.localeCompare(b.symbol);
				}

				if (header === 'price') {
					return isDescending
						? (b.price
								?.sub(a?.price ?? BigNum.zero(PRICE_PRECISION_EXP))
								.toNum() ?? 0)
						: (a.price
								?.sub(b?.price ?? BigNum.zero(PRICE_PRECISION_EXP))
								.toNum() ?? 0);
				}

				if (header === 'priceChangePct') {
					// A market with no 24h window sorts as flat rather than poisoning
					// the comparator with NaN.
					const aChange = Number.isFinite(a?.pricePercentChange)
						? (a.pricePercentChange as number)
						: 0;
					const bChange = Number.isFinite(b?.pricePercentChange)
						? (b.pricePercentChange as number)
						: 0;

					return isDescending ? bChange - aChange : aChange - bChange;
				}

				if (header === 'volume') {
					return isDescending
						? (b?.volume ?? 0) - (a?.volume ?? 0)
						: (a?.volume ?? 0) - (b?.volume ?? 0);
				}

				if (header === 'openInterest') {
					return isDescending
						? (b?.openInterest ?? 0) - (a?.openInterest ?? 0)
						: (a?.openInterest ?? 0) - (b?.openInterest ?? 0);
				}

				if (header === 'funding') {
					return isDescending
						? (b?.funding24H ?? 0) - (a?.funding24H ?? 0)
						: (a?.funding24H ?? 0) - (b?.funding24H ?? 0);
				}

				if (header === 'priceLow24h') {
					return isDescending
						? (b?.low ?? 0) - (a?.low ?? 0)
						: (a?.low ?? 0) - (b?.low ?? 0);
				}

				if (header === 'priceHigh24h') {
					return isDescending
						? (b?.high ?? 0) - (a?.high ?? 0)
						: (a?.high ?? 0) - (b?.high ?? 0);
				}

				return 0;
			});
		}

		return sortedMarkets;
	}, [
		selectedCategory,
		defaultSortedPerpMarkets,
		searchValue,
		sort,
		isFavoritesSticky,
	]);
}
